import React, { useState } from 'react';
import { Box, TextField, Typography, Alert, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSignupWizard } from '../../../contexts/SignupWizardContext';
import { setPassword } from '../../../utils/signupApi';
import { validatePassword } from '../../../utils/passwordValidation';
import { PasswordStrengthIndicator } from '../../../components/PasswordStrengthIndicator';
import { en } from '../../../translations/en';
import { usePasswordVisibility } from '../../../hooks/usePasswordVisibility';

interface SignupStep5PasswordProps {
  onNext: () => void;
  onBack: () => void;
}

export const SignupStep5Password: React.FC<SignupStep5PasswordProps> = ({
  onNext,
  onBack: _onBack,
}) => {
  const { state, setPasswordSet, setError, setLoading } = useSignupWizard();

  const [password, setPasswordLocal] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { showPassword, inputType: passwordInputType, toggleVisibility: togglePasswordVisibility } = usePasswordVisibility();
  const { showPassword: showConfirmPassword, inputType: confirmPasswordInputType, toggleVisibility: toggleConfirmPasswordVisibility } = usePasswordVisibility();

  const handleSubmit = async () => {
    // Validate passwords match
    if (password !== confirmPassword) {
      setLocalError(en.signup.validation.passwordMismatch);
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setLocalError(passwordValidation.errors.join('. '));
      return;
    }

    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }

    setLoading(true);
    setError(null);
    setLocalError(null);

    try {
      const response = await setPassword(state.signupToken, password);

      if (response.success) {
        setPasswordSet(true);
        onNext();
      } else {
        setLocalError(response.error || response.message || 'Failed to set password');
      }
    } catch (error) {
      const message = (error as Error).message || 'An unexpected error occurred';
      setLocalError(message);
    } finally {
      setLoading(false);
    }
  };

  // Expose submit handler for parent component
  React.useEffect(() => {
    (window as unknown as { __signupStep5Submit: () => Promise<void> }).__signupStep5Submit =
      handleSubmit;
    return () => {
      delete (window as unknown as { __signupStep5Submit?: () => Promise<void> })
        .__signupStep5Submit;
    };
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create Your Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose a strong password for your account. This will be used to sign in along with your
        email.
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      <TextField
        fullWidth
        label={en.signup.form.password}
        type={passwordInputType}
        value={password}
        onChange={(e) => setPasswordLocal(e.target.value)}
        margin="normal"
        required
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label="toggle new password visibility"
                onClick={togglePasswordVisibility}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <PasswordStrengthIndicator password={password} compact={true} />

      <TextField
        fullWidth
        label={en.signup.form.confirmPassword}
        type={confirmPasswordInputType}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        margin="normal"
        required
        autoComplete="new-password"
        error={confirmPassword.length > 0 && password !== confirmPassword}
        helperText={
          confirmPassword.length > 0 && password !== confirmPassword
            ? en.signup.validation.passwordMismatch
            : ''
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label="toggle confirm password visibility"
                onClick={toggleConfirmPasswordVisibility}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" component="div">
          Password requirements:
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>At least 8 characters long</li>
            <li>Contains uppercase letter</li>
            <li>Contains lowercase letter</li>
            <li>Contains number</li>
            <li>Contains special character (!@#$%^&* etc.)</li>
          </ul>
        </Typography>
      </Alert>
    </Box>
  );
};

export default SignupStep5Password;
